import { useCallback, useContext, useEffect, useRef } from 'react'
import { RequestContext } from '../context/RequestContext'

const MAX_IMAGE_BYTES = 10485760     // 10MB
const MAX_VIDEO_BYTES = 524288000    // 500MB

// Photo/video attachments for a booking request, shared by the booking
// forms' submit steps. Feeds FileUploadSection (components/commons/
// BookingFormComponents.js).
//
// RequestContext keeps two lists: filesToAttach (previews) and filesToUpload
// (the File objects sent after the request is created). They must stay in the
// same order so a removed preview also leaves the upload list. Previews used
// to be data URLs read asynchronously with FileReader, which could land out of
// order, and removing one left its file in the upload list. Object URLs are
// created synchronously, so both lists are appended in one step.
export const useBookingAttachments = ({ onError } = {}) => {
    const { filesToAttach, setFilesToAttach, setFilesToUpload } = useContext(RequestContext)

    // Object URLs created here. Released on remove, and on unmount only if no
    // longer listed: Back unmounts the submit step but keeps the attachments,
    // and their previews must still work when the customer returns.
    const createdUrls = useRef(new Set())
    const latestAttached = useRef(filesToAttach)
    useEffect(() => {
        latestAttached.current = filesToAttach
    }, [filesToAttach])
    useEffect(() => {
        const urls = createdUrls.current
        return () => {
            const stillListed = new Set((latestAttached.current || []).map(f => f.media))
            urls.forEach(url => {
                if (!stillListed.has(url)) URL.revokeObjectURL(url)
            })
            urls.clear()
        }
    }, [])

    const addFiles = useCallback((fileList, maxBytes, sizeError) => {
        const files = [...(fileList || [])]
        if (files.length === 0) return
        if (files.some(file => file.size > maxBytes)) {
            onError?.(sizeError)
            return
        }
        const previews = files.map(file => {
            const url = URL.createObjectURL(file)
            createdUrls.current.add(url)
            return { media: url, fileType: file.type }
        })
        setFilesToAttach(prev => [...prev, ...previews])
        setFilesToUpload(prev => [...prev, ...files.map(file => ({ media: file, fileType: file.type }))])
    }, [onError, setFilesToAttach, setFilesToUpload])

    const onImageUpload = useCallback((e) => {
        addFiles(e.target.files, MAX_IMAGE_BYTES, {
            header: "File size exceeded",
            body: "Please try again with a file size less than 10MB."
        })
        // Allow picking the same file again after removing it
        e.target.value = ""
    }, [addFiles])

    const onVideoUpload = useCallback((e) => {
        addFiles(e.target.files, MAX_VIDEO_BYTES, {
            header: "File size exceeded",
            body: "Please try again with a file size less than 500MB."
        })
        e.target.value = ""
    }, [addFiles])

    const onRemove = useCallback((index) => {
        const url = filesToAttach[index]?.media
        // May come from an earlier mount of the step, so check the scheme
        // rather than createdUrls
        if (typeof url === "string" && url.startsWith("blob:")) {
            URL.revokeObjectURL(url)
            createdUrls.current.delete(url)
        }
        setFilesToAttach(prev => prev.filter((_, i) => i !== index))
        setFilesToUpload(prev => prev.filter((_, i) => i !== index))
    }, [filesToAttach, setFilesToAttach, setFilesToUpload])

    return { filesToAttach, onImageUpload, onVideoUpload, onRemove }
}
